import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Eye, EyeOff, Check } from 'lucide-react';
import { useSignup } from '../hooks/useAuth';
import { handleApiError } from '../../../services/api';
import { ROUTES } from '../../../config/routes';
import { GoogleLoginButton } from './GoogleLoginButton';
import { PasswordStrengthBar } from './PasswordStrengthBar';
import { Input } from '../../../components/ui/input';
import { Button } from '../../../components/ui/button';
import { useAuthLayoutConfig } from '../../../components/layout/AuthLayout';

const signupSchema = z.object({
  name: z.string().min(1, '名前を入力してください').max(100, '名前は100文字以内で入力してください'),
  email: z.string().email('有効なメールアドレスを入力してください'),
  password: z
    .string()
    .min(8, 'パスワードは8文字以上で入力してください')
    .regex(/[a-z]/, '小文字を含めてください')
    .regex(/[A-Z]/, '大文字を含めてください')
    .regex(/[0-9]/, '数字を含めてください')
    .regex(/[^a-zA-Z0-9]/, '記号を含めてください'),
});

type SignupFormData = z.infer<typeof signupSchema>;

const SIGNUP_CHECK_ICON = <Check className="h-4 w-4" />;

const SIGNUP_FEATURES = [
  { icon: SIGNUP_CHECK_ICON, text: '月300メッセージまで利用可能' },
  { icon: SIGNUP_CHECK_ICON, text: 'チャットBOTを3つまで作成' },
  { icon: SIGNUP_CHECK_ICON, text: '全機能をお試し可能' },
  { icon: SIGNUP_CHECK_ICON, text: 'いつでも有料プランに変更' },
];

export const SignupForm: React.FC = () => {
  const [showPassword, setShowPassword] = React.useState(false);
  const { mutate: signup, isPending } = useSignup();
  const [isRateLimited, setIsRateLimited] = React.useState(false);
  const { setConfig } = useAuthLayoutConfig();

  React.useLayoutEffect(() => {
    setConfig({
      visualTagline: 'チャットBOTが、3分で作れる。\nまずは14日間無料でお試しください。',
      visualBadge: { title: '14日間無料トライアル', sub: 'クレジットカード登録不要' },
      visualFeatures: SIGNUP_FEATURES,
    });
    return () => setConfig({});
  }, [setConfig]);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<SignupFormData>({
    resolver: zodResolver(signupSchema),
  });

  const password = watch('password');

  const onSubmit = (data: SignupFormData) => {
    sessionStorage.setItem('_eb_auto', data.password);
    signup({
      email: data.email,
      password: data.password,
      name: data.name,
    }, {
      onError: (error) => {
        const apiError = handleApiError(error);
        if (apiError.code === 'RATE_LIMIT_EXCEEDED') {
          const axiosErr = error as any;
          const retryAfter = axiosErr?.response?.data?.retry_after ?? 300;
          setIsRateLimited(true);
          setTimeout(() => setIsRateLimited(false), retryAfter * 1000);
        }
      },
    });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate className="w-full">
      <div className="mb-4">
        <label htmlFor="signup-name" className="block text-sm font-medium mb-1.5">お名前</label>
        <Input
          {...register('name')}
          id="signup-name"
          type="text"
          placeholder="田中 太郎"
          autoComplete="name"
          className="!text-[16px] md:!text-sm"
          aria-invalid={!!errors.name}
          aria-describedby="signup-name-error"
        />
        {errors.name && (
          <p id="signup-name-error" className="text-sm text-red-500 mt-1">
            {errors.name.message}
          </p>
        )}
      </div>

      <div className="mb-4">
        <label htmlFor="signup-email" className="block text-sm font-medium mb-1.5">メールアドレス</label>
        <Input
          {...register('email')}
          id="signup-email"
          type="email"
          autoComplete="email"
          className="!text-[16px] md:!text-sm"
          aria-invalid={!!errors.email}
          aria-describedby="signup-email-error"
          data-testid="signup-email-input"
        />
        {errors.email && (
          <p id="signup-email-error" className="text-sm text-red-500 mt-1">
            {errors.email.message}
          </p>
        )}
      </div>

      <div className="mb-5">
        <label htmlFor="signup-password" className="block text-sm font-medium mb-1.5">パスワード</label>
        <div className="relative">
          <Input
            {...register('password')}
            id="signup-password"
            type={showPassword ? 'text' : 'password'}
            placeholder="パスワードを入力"
            autoComplete="new-password"
            className="pr-10 !text-[16px] md:!text-sm"
            aria-invalid={!!errors.password}
            aria-describedby="signup-password-error"
            data-testid="signup-password-input"
          />
          <button
            type="button"
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            onClick={() => setShowPassword(!showPassword)}
            tabIndex={-1}
          >
            {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </div>
        {errors.password && (
          <p id="signup-password-error" className="text-sm text-red-500 mt-1">
            {errors.password.message}
          </p>
        )}
        <p className="text-xs text-gray-400 mt-1">8文字以上・英大文字・数字を含む</p>
        <PasswordStrengthBar password={password || ''} />{/* data-testid="password-strength-bar" */}
      </div>

      {/* confirmPassword testid保持 (非表示) */}
      <input type="hidden" data-testid="signup-confirm-password-input" />

      <Button
        type="submit"
        className="w-full !h-auto !py-3.5 !text-base !font-bold !rounded-lg mb-4 md:!py-3 md:!text-[15px]"
        disabled={isPending || isRateLimited}
        data-testid="signup-submit-button"
      >
        {isPending ? '登録中...' : isRateLimited ? 'しばらくお待ちください...' : '無料ではじめる'}
      </Button>

      {/* 同意文 (法務3点) */}
      <p className="text-center text-[11px] text-gray-500 leading-relaxed mb-5">
        <a href="https://entry-chat-bot.com/terms" target="_blank" rel="noreferrer" className="text-primary underline">利用規約</a>
        ・
        <a href="https://entry-chat-bot.com/privacy" target="_blank" rel="noreferrer" className="text-primary underline">プライバシーポリシー</a>
        ・
        <a href="https://entry-chat-bot.com/legal" target="_blank" rel="noreferrer" className="text-primary underline">特定商取引法</a>
        に同意のうえ登録します
      </p>

      {/* divider */}
      <div className="flex items-center gap-4 mb-4">
        <div className="flex-1 h-px bg-border"></div>
        <span className="text-xs text-gray-500">または</span>
        <div className="flex-1 h-px bg-border"></div>
      </div>

      <GoogleLoginButton mode="signup" testId="google-signup-button" />

      <div className="text-center mt-6">
        <p className="text-sm text-text-secondary">
          すでにアカウントをお持ちの方は{' '}
          <RouterLink
            to={ROUTES.LOGIN}
            className="text-sm text-primary hover:underline font-semibold"
          >
            ログイン
          </RouterLink>
        </p>
      </div>
    </form>
  );
};
